import * as IC from "@fluentui/react-icons";
import * as FC from "@fluentui/react-components";
import * as FDP from "@fluentui/react-datepicker-compat";
import { useState, useContext, useEffect, Fragment, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Context } from "../../context";
import { TurbineTaskStatusView } from "../../components/TurbineTaskStatusView";
import {
    buildFilter,
    cvtLeafPart,
    cvtPriority,
    cvtRawDataStatus,
    cvtTaskStatus,
    cvtTaskRecheckType,
    idString,
    idRecord,
} from "../../db_utils";
import { getCurrentUser } from "../../services/login";
import { toDateString } from "../../date_utils";
import { getCurrentFieldFromStorage, getCurrentFieldIdFromNew } from "../../workspace";
import { apiClient } from "../../services/api";
import { customerService } from "../../services/customer_service";
import { SessionEquipmentPanel } from "../../components/equipment/session_equipment_panel";
import { resolveEquipmentPermissions } from "../../services/equipment_service";
import { groupTasksBySession } from "./task_session_summary";

const useStyles = FC.makeStyles({
    root: {
        height: "100%",
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
        ...FC.shorthands.padding("10px"),
        ...FC.shorthands.gap("10px"),
    },
    selector: {
        backgroundColor: FC.tokens.colorBrandBackground2,
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
        ...FC.shorthands.padding("10px"),
        ...FC.shorthands.margin("10px"),
    },
    endAlign: {
        textAlign: "end",
        color: "white",
        backgroundColor: FC.tokens.colorBrandBackground,
        ...FC.shorthands.padding("5px"),
    },
});

const FieldSelector = ({ onSelect }) => {
    let { apiClient } = useContext(Context);
    let [fields, setFields] = useState([]);
    let [field, setField] = useState(null);

    const listFields = async () => {
        try {
            const fieldItems = await apiClient.getFieldItems();
            setFields(fieldItems || []);
        } catch (error) {
            console.error("Failed to load field items:", error);
            setFields([]);
        }
    };

    useEffect(() => {
        listFields();
    }, []);

    useEffect(() => {
        if (onSelect) onSelect(field);
    }, [field]);

    let styles = useStyles();
    return (
        <div className={styles.selector}>
            <h3>1.选择风场</h3>
            <FC.RadioGroup
                style={{
                    flex: 1,
                }}
                onChange={(_, data) => {
                    let field = fields.find(
                        (it) => idString(it.id) === idString(data.value),
                    );
                    setField({ ...field });
                    // console.log("select", data);
                }}
            >
                {fields.map((field, idx) => {
                    return (
                        <FC.Radio
                            key={idx}
                            value={idString(field.id)}
                            label={field?.name}
                        />
                    );
                })}
            </FC.RadioGroup>
            <div
                style={{
                    flex: 1,
                    backgroundColor: "white",
                    borderRadius: "5px",
                    boxShadow: FC.tokens.shadow8,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-around",
                    padding: 10,
                }}
            >
                {field ? (
                    <Fragment>
                        <div
                            style={{
                                display: "grid",
                                gap: 20,
                                gridTemplateColumns: "auto 1fr",
                            }}
                        >
                            <h3 className={styles.endAlign}>编号</h3>
                            <h3>{field?.seq}</h3>
                            <span className={styles.endAlign}>名称</span>
                            <span>{field?.name}</span>
                            <span className={styles.endAlign}>地址</span>
                            <span>{field?.address}</span>
                            <span className={styles.endAlign}> 联系人</span>
                            <span>{field?.contact}</span>
                            <span className={styles.endAlign}>联系电话</span>
                            <span>{field?.contact_phone}</span>
                            <span className={styles.endAlign}>更新时间</span>
                            <span>
                                {new Date(
                                    field.updated_at,
                                ).toLocaleDateString()}
                            </span>
                        </div>
                    </Fragment>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "36px",
                            color: "#ccc",
                        }}
                    >
                        未选择
                    </div>
                )}
            </div>
        </div>
    );
};

const TurbineSelectorSimple = ({ field, onSelect, initialSelected }) => {
    let { apiClient } = useContext(Context);
    let [turbines, setTurbines] = useState([]);
    let [filter] = useState(null);
    let [selectItems, setSelectedItems] = useState(
        new Set(initialSelected || []),
    );
    console.log("selectItems:", selectItems);
    let [selectedTurbines, setSelectedTurbines] = useState(new Set());

    const listTurbine = async () => {
        try {
            if (!field || !field.id) {
                setTurbines([]);
                return;
            }
            
            // Get turbines filtered by field from API
            const fieldTurbines = await apiClient.getTurbines({ fieldId: field.id });
            
            setTurbines(fieldTurbines || []);
        } catch (error) {
            console.error("Failed to load turbines:", error);
            setTurbines([]);
        }
    };

    useEffect(() => {
        if (field) {
            listTurbine();
        } else {
            setTurbines([]);
        }
    }, [field]);

    // useEffect(() => {
    //     setSelectedItems(new Set());
    // }, [field]);

    useEffect(() => {
        setSelectedTurbines([...selectItems.keys()]);
    }, [selectItems]);

    useEffect(() => {
        if (onSelect) {
            onSelect(selectedTurbines);
        }
    }, [selectedTurbines]);

    const columns = [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => {
                return "风机编号";
            },
            renderCell: (item) => {
                return <FC.TableCellLayout>{item.seq}</FC.TableCellLayout>;
            },
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "风机名称";
            },
            renderCell: (item) => {
                return <FC.TableCellLayout>{item.name}</FC.TableCellLayout>;
            },
        }),
        FC.createTableColumn({
            columnId: "model.seq",
            renderHeaderCell: () => {
                return "型号";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>{item.model?.seq}</FC.TableCellLayout>
                );
            },
        }),
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <FC.Input
                appearance="underline"
                contentBefore={<IC.SearchRegular />}
                placeholder="快速搜索..."
            />
            <div style={{
                flex: 1,
                maxHeight: '400px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginTop: '10px'
            }}>
                <FC.DataGrid
                    items={turbines}
                    columns={columns}
                    defaultSelectedItems={selectItems}
                    selectionMode="multiselect"
                    onSelectionChange={(_, data) => {
                        console.log("data:...", data);
                        setSelectedItems(data?.selectedItems);
                    }}
                    getRowId={(item) => idString(item.id)}
                >
                    <FC.DataGridHeader>
                        <FC.DataGridRow>
                            {({ renderHeaderCell }, i) => (
                                <FC.DataGridHeaderCell key={i}>
                                    {renderHeaderCell()}
                                </FC.DataGridHeaderCell>
                            )}
                        </FC.DataGridRow>
                    </FC.DataGridHeader>
                    <FC.DataGridBody>
                        {({ item }) => (
                            <FC.DataGridRow key={item.seq}>
                                {({ renderCell }) => (
                                    <FC.DataGridCell>
                                        {renderCell(item)}
                                    </FC.DataGridCell>
                                )}
                            </FC.DataGridRow>
                        )}
                    </FC.DataGridBody>
                </FC.DataGrid>
            </div>
        </div>
    );
};

const TurbineSelector = ({ onSelect, field }) => {
    let { apiClient } = useContext(Context);
    let [turbines, setTurbines] = useState([]);
    let [filter, setFilter] = useState(null);
    let [selectItems, setSelectedItems] = useState(new Set());
    let [selectedTurbines, setSelectedTurbines] = useState(new Set());

    const searchable = ["seq", "name", "model.seq", "model.manu.name"];

    const columns = [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => {
                return "风机编号";
            },
            renderCell: (item) => {
                return <FC.TableCellLayout>{item.seq}</FC.TableCellLayout>;
            },
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "风机名称";
            },
            renderCell: (item) => {
                return <FC.TableCellLayout>{item?.name}</FC.TableCellLayout>;
            },
        }),
        FC.createTableColumn({
            columnId: "model.seq",
            renderHeaderCell: () => {
                return "型号";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>{item.model.seq}</FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "model.manu.name",
            renderHeaderCell: () => {
                return "厂商";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.model.manu.name}
                    </FC.TableCellLayout>
                );
            },
        }),
    ];

    const listTurbine = async () => {
        try {
            if (!field || !field.id) {
                setTurbines([]);
                return;
            }
            
            // Get turbines filtered by field from API with search parameter
            const params = { fieldId: field.id };
            if (filter) {
                params.search = filter;
            }
            
            const fieldTurbines = await apiClient.getTurbines(params);
            
            setTurbines(fieldTurbines || []);
        } catch (error) {
            console.error("Failed to load turbines:", error);
            setTurbines([]);
        }
    };

    useEffect(() => {
        if (field && apiClient) {
            listTurbine();
        } else {
            setTurbines([]);
        }
    }, [field, apiClient, filter]);

    useEffect(() => {
        setSelectedItems(new Set());
    }, [field]);

    useEffect(() => {
        setSelectedTurbines([...selectItems.keys()]);
    }, [selectItems]);

    useEffect(() => {
        if (onSelect) {
            onSelect(selectedTurbines);
        }
    }, [selectedTurbines]);

    let styles = useStyles();
    return (
        <div className={styles.selector} style={{ opacity: field ? 1.0 : 0.5 }}>
            <h3>2.选择风机</h3>
            <FC.Input
                contentBefore={<IC.SearchRegular />}
                placeholder="输入检索条件"
                onChange={(evt) => {
                    setFilter(evt.target.value);
                }}
            />
            <FC.DataGrid
                items={turbines}
                columns={columns}
                selectedItems={selectItems}
                selectionMode="multiselect"
                onSelectionChange={(_, data) => {
                    setSelectedItems(data?.selectedItems);
                }}
                getRowId={(item) => idString(item.id)}
            >
                <FC.DataGridHeader>
                    <FC.DataGridRow>
                        {({ renderHeaderCell }, i) => (
                            <FC.DataGridHeaderCell key={i}>
                                {renderHeaderCell()}
                            </FC.DataGridHeaderCell>
                        )}
                    </FC.DataGridRow>
                </FC.DataGridHeader>
                <FC.DataGridBody>
                    {({ item }) => (
                        <FC.DataGridRow key={item.seq}>
                            {({ renderCell }) => (
                                <FC.DataGridCell>
                                    {renderCell(item)}
                                </FC.DataGridCell>
                            )}
                        </FC.DataGridRow>
                    )}
                </FC.DataGridBody>
            </FC.DataGrid>

            {/* {JSON.stringify(turbines)} */}
        </div>
    );
};

const SubWorkerSelector = ({ onSelect, users }) => {
    let [user, setUser] = useState(null);

    const columns = [
        FC.createTableColumn({
            columnId: "account",
            renderHeaderCell: () => {
                return "账户名";
            },
            renderCell: (item) => {
                return item.account;
            },
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "姓名";
            },
            renderCell: (item) => {
                return item.name;
            },
        }),
        FC.createTableColumn({
            columnId: "department",
            renderHeaderCell: () => {
                return "部门";
            },
            renderCell: (item) => {
                return item.department;
            },
        }),
    ];

    useEffect(() => {
        if (onSelect) {
            onSelect(user);
        }
    }, [user]);

    return (
        <div
            style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <FC.DataGrid
                items={users}
                columns={columns}
                onSelectionChange={(_, data) => {
                    let selected = [...data.selectedItems.keys()]?.[0] || null;
                    setUser(selected);
                }}
                selectionMode="single"
                getRowId={(item) => {
                    return idString(item.id);
                }}
            >
                <FC.DataGridHeader>
                    <FC.DataGridRow>
                        {({ renderHeaderCell }) => (
                            <FC.DataGridHeaderCell>
                                {renderHeaderCell()}
                            </FC.DataGridHeaderCell>
                        )}
                    </FC.DataGridRow>
                </FC.DataGridHeader>
                <div style={{ overflowY: "auto" }}>
                    <FC.DataGridBody>
                        {({ item, rowId }) => {
                            return (
                                <FC.DataGridRow key={rowId}>
                                    {({ renderCell }) => {
                                        return (
                                            <FC.DataGridCell>
                                                {renderCell(item)}
                                            </FC.DataGridCell>
                                        );
                                    }}
                                </FC.DataGridRow>
                            );
                        }}
                    </FC.DataGridBody>
                </div>
            </FC.DataGrid>
        </div>
    );
};

const WorkerSelector = ({ onSelect, field, turbines }) => {
    let [users, setUsers] = useState([]);
    let { apiClient } = useContext(Context);
    let [filter, setFilter] = useState(null);
    let [selectedUser, setSelectedUser] = useState(null);

    const searchable = ["account", "name", "department"];

    const listUsers = async () => {
        try {
            // Get all users from API
            const allUsers = await apiClient.getUsers();
            
            // Filter users who have access to the current field
            let filteredUsers = allUsers || [];
            if (field && field.id) {
                // Filter users who have this field in their fields array
                filteredUsers = filteredUsers.filter(user => 
                    user.fields && Array.isArray(user.fields) && 
                    user.fields.some(f => 
                        (typeof f === 'string' ? f : f.id) === field.id
                    )
                );
            }
            
            // Apply text filter if provided
            if (filter && filteredUsers.length > 0) {
                const filterLower = filter.toLowerCase();
                filteredUsers = filteredUsers.filter(user => {
                    // Search across searchable fields
                    return (
                        user.account?.toLowerCase().includes(filterLower) ||
                        user.name?.toLowerCase().includes(filterLower) ||
                        user.department?.toLowerCase().includes(filterLower)
                    );
                });
            }
            
            setUsers(filteredUsers);
        } catch (error) {
            console.error("Failed to load users:", error);
            setUsers([]);
        }
    };

    useEffect(() => {
        if (apiClient) {
            listUsers();
        }
    }, [field, apiClient, filter]);

    useEffect(() => {
        if (onSelect) {
            onSelect(selectedUser);
        }
    }, [selectedUser]);

    let styles = useStyles();

    return (
        <div
            className={styles.selector}
            style={{ opacity: field && turbines?.length ? 1.0 : 0.5 }}
        >
            <h3>3.选择人员</h3>
            <FC.Input
                contentBefore={<IC.SearchRegular />}
                placeholder="输入人员检索条件"
                onChange={(evt) => {
                    setFilter(evt.target.value);
                }}
            />
            <h4>巡检人员</h4>
            <SubWorkerSelector
                {...{
                    users,
                    onSelect: (user) => {
                        setSelectedUser({ ...selectedUser, checker: user });
                    },
                }}
            />
            <h4>复核人员</h4>
            <SubWorkerSelector
                {...{
                    users,
                    onSelect: (user) => {
                        setSelectedUser({ ...selectedUser, rechecker: user });
                    },
                }}
            />
        </div>
    );
};

export const TaskNew = () => {
    let styles = useStyles();
    let { apiClient, currentField } = useContext(Context);
    let [field, setField] = useState(null);
    let [selectedTurbines, setSelectedTurbines] = useState([]);
    // Track owners selected in the creation page
    let [selectedOwners, setSelectedOwners] = useState({ checker: null, rechecker: null });
    
    useEffect(() => {
        // Try new field system first, then fall back to old
        if (currentField) {
            setField(currentField);
        } else {
            let field = getCurrentFieldFromStorage();
            if (field) {
                setField(field);
            } else {
                console.log("No field available from storage for TaskNew");
                setField(null);
            }
        }
    }, [currentField]);

    return (
        <div className={styles.root}>
            <div
                style={{
                    display: "grid",
                    flex: 1,
                    overflowY: "auto",
                    gridTemplateColumns: "1fr 1fr 1fr",
                }}
            >
                <FieldSelector
                    onSelect={(selectedField) => {
                        setField(selectedField);
                        setSelectedTurbines([]); // Clear selected turbines when field changes
                    }}
                />
                <TurbineSelector
                    {...{
                        field,
                        onSelect: (turbines) => {
                            setSelectedTurbines(turbines);
                        },
                    }}
                />
                <WorkerSelector
                    {...{ field, turbines: selectedTurbines }}
                    onSelect={(users) => {
                        // users is an object like { checker: user?, rechecker: user? }
                        setSelectedOwners(users || {});
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    gap: 10,
                }}
            >
                <FC.Button appearance="primary">创建任务</FC.Button>
                <FC.Button appearance="default">取消创建</FC.Button>
            </div>
        </div>
    );
};

const useStyleTaskDetail = FC.makeStyles({
    root: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.padding("10px"),
        ...FC.shorthands.gap("10px"),
    },
    cell: {
        display: "flex",
        flexDirection: "column",
        // justifyContent: "space-between",
        overflowY: "auto",
        boxShadow: FC.tokens.shadow4,
        ...FC.shorthands.gap("5px"),
        ...FC.shorthands.margin("10px"),
        ...FC.shorthands.padding("10px"),
    },
});

const FieldDropdown = ({ setField }) => {
    let { apiClient } = useContext(Context);
    let [fields, setFields] = useState([]);

    const listFields = async () => {
        try {
            const fieldItems = await apiClient.getFieldItems();
            setFields(fieldItems || []);
        } catch (error) {
            console.error("Failed to load field items:", error);
            setFields([]);
        }
    };

    useEffect(() => {
        if (apiClient) {
            listFields();
        }
    }, [apiClient]);

    return (
        <FC.Dropdown
            onOptionSelect={(_, f) => {
                setField(f.optionValue);
            }}
            placeholder="切换风场重新选择风机..."
        >
            {fields.map((f, i) => {
                return (
                    <FC.Option key={f.id} value={f}>
                        {f.name}
                    </FC.Option>
                );
            })}
        </FC.Dropdown>
    );
};

// Which slot each switch fills. The frontend does NOT decide who qualifies — it asks the
// server per slot, so this list is only about which question to ask.
const ASSIGNMENT_SLOTS = ["ownerCreater", "ownerChecker", "ownerAnnotator", "ownerRechecker"];

// Exported for tests. The tenant-switch race it guards cannot be reproduced through the page:
// it needs one tenant's four replies to land after the next tenant's, which means driving the
// promises directly.
export const WorkerSelectorSimple = ({ onSelect, task, tenantId }) => {
    let { apiClient } = useContext(Context);
    let [users, setUsers] = useState([]);
    let [eligible, setEligible] = useState({});
    let [ownerCreater, setOwnerCreater] = useState(task?.owner_creater?.id || null);
    let [ownerChecker, setOwnerChecker] = useState(task?.owner_checker?.id || null);
    let [ownerAnnotator, setOwnerAnnotator] = useState(task?.owner_annotator?.id || null);
    let [ownerReChecker, setOwnerReChecker] = useState(task?.owner_rechecker?.id || null);
    let [filter, setFilter] = useState(null);

    const searchable = ["account", "name", "department"];

    // Until 2026-08-16 this called getUsers() with no arguments and handed the one list to all
    // four switches. For a platform administrator that list is EVERY tenant's accounts, in every
    // role — so the selector offered cross-tenant and wrong-role assignees, and the server took
    // them (it validated nothing). The server refuses them now; this asks the same server which
    // accounts qualify, one request per slot, rather than restating the rule here where it would
    // drift.
    // Four requests go out per load, and switching tenants starts four more before the first
    // four have answered. Without a generation stamp the older tenant's replies can land last
    // and repopulate the selector with accounts from the tenant the operator just left.
    const requestRef = useRef(0);
    const previousTenantRef = useRef(tenantId);
    const loadedOnceRef = useRef(false);

    // Clearing inside the success path is too late. Between the switch and the new tenant's
    // replies the grid still showed the previous tenant's accounts with a selection still live,
    // and if those replies failed it stayed that way — the operator could submit from a screen
    // belonging to a tenant they had left, and only the server said no. Drop it all the moment
    // the tenant changes, not when its replacement arrives.
    //
    // Guarded on an actual change so the first render keeps the selection the task came with:
    // clearing unconditionally here would wipe the assignees of every task being edited.
    useEffect(() => {
        const previous = previousTenantRef.current;
        previousTenantRef.current = tenantId;
        // An edit screen mounts before the page has resolved its tenant and receives one a
        // moment later. That is initialisation, not a switch — treating it as one wiped the
        // assignees the task was opened with, so an unrelated edit silently unassigned
        // everybody. Only a move BETWEEN two resolved tenants clears.
        if (!previous || previous === tenantId) return;
        setUsers([]);
        setEligible({});
        setOwnerCreater(null);
        setOwnerChecker(null);
        setOwnerAnnotator(null);
        setOwnerReChecker(null);
    }, [tenantId]);

    // TaskDetail renders with task=null and mounts this component immediately, so the useState
    // seeds above run against nothing. The task arrives a moment later and useState does not
    // re-run — and by then the parent has already declined to initialise its own taskInfo,
    // because the onSelect below fired on mount and filled it with four nulls. Saving after
    // that sent four zeroes and unassigned everyone who was on the task.
    //
    // Seeded once per task id, and deliberately declared AFTER the tenant-switch effect so that
    // when a switch and a task load land in the same commit the task's own assignees win.
    const seededTaskRef = useRef(task?.id ?? null);
    useEffect(() => {
        const loadedTaskId = task?.id ?? null;
        if (loadedTaskId === null || loadedTaskId === seededTaskRef.current) return;
        seededTaskRef.current = loadedTaskId;
        setOwnerCreater(task?.owner_creater?.id ?? null);
        setOwnerChecker(task?.owner_checker?.id ?? null);
        setOwnerAnnotator(task?.owner_annotator?.id ?? null);
        setOwnerReChecker(task?.owner_rechecker?.id ?? null);
    }, [task]);

    const listUser = async () => {
        const seq = ++requestRef.current;
        if (!tenantId) {
            // No tenant resolved yet: offer nobody. An unscoped list is what caused the defect.
            setUsers([]);
            setEligible({});
            return;
        }
        try {
            const perSlot = await Promise.all(ASSIGNMENT_SLOTS.map((slot) =>
                apiClient.getUsers({ tenantId, assignableFor: slot })
                    .then((rows) => [slot, rows || []])
            ));
            if (seq !== requestRef.current) return;

            const byId = new Map();
            const sets = {};
            for (const [slot, rows] of perSlot) {
                sets[slot] = new Set(rows.map((user) => user.id));
                for (const user of rows) byId.set(user.id, user);
            }

            let merged = [...byId.values()];
            if (filter) {
                const filterLower = filter.toLowerCase();
                merged = merged.filter(user =>
                    user.name?.toLowerCase().includes(filterLower) ||
                    user.account?.toLowerCase().includes(filterLower)
                );
            }

            setUsers(merged);
            setEligible(sets);

            // A selection made under the previous tenant survives the switch otherwise. Greying
            // the switch out is not enough: the id stays in state, onSelect keeps putting it in
            // the submit body, and the only thing that stops it is the server rejecting the
            // request. Drop anything the new eligibility does not contain.
            //
            // Skipped on the first load. An existing task may hold an assignee made before the
            // rule existed; clearing it here would turn "edit the notes" into "unassign the
            // annotator" without anyone asking for it. The server validates only the slots a
            // request actually changes, for the same reason.
            if (loadedOnceRef.current) {
                setOwnerCreater((current) => (current && sets.ownerCreater?.has(current) ? current : null));
                setOwnerChecker((current) => (current && sets.ownerChecker?.has(current) ? current : null));
                setOwnerAnnotator((current) => (current && sets.ownerAnnotator?.has(current) ? current : null));
                setOwnerReChecker((current) => (current && sets.ownerRechecker?.has(current) ? current : null));
            }
            loadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestRef.current) return;
            console.error("Failed to load assignable users:", error);
            setUsers([]);
            setEligible({});
        }
    };

    useEffect(() => {
        listUser();
        // Bumping the generation on unmount/re-run makes any in-flight reply from the previous
        // tenant a no-op rather than a late overwrite.
        return () => { requestRef.current += 1; };
    }, [filter, tenantId]);

    useEffect(() => {
        if (onSelect) {
            const kv = {};
            kv["owner_creater"] = ownerCreater;
            const creater = users.find(u => u.id === ownerCreater);
            kv["owner_creater_name"] = creater?.name;
            kv["owner_checker"] = ownerChecker;
            const checker = users.find(u => u.id === ownerChecker);
            kv["owner_checker_name"] = checker?.name;
            kv["owner_annotator"] = ownerAnnotator;
            const annotator = users.find(u => u.id === ownerAnnotator);
            kv["owner_annotator_name"] = annotator?.name;
            kv["owner_rechecker"] = ownerReChecker;
            const rechecker = users.find(u => u.id === ownerReChecker);
            kv["owner_rechecker_name"] = rechecker?.name;
            if (Object.keys(kv).length > 0) onSelect(kv);
        }
    }, [ownerCreater, ownerChecker, ownerAnnotator, ownerReChecker, users]);

    const columns = [
        FC.createTableColumn({
            columnId: "account",
            renderHeaderCell: () => "账户",
            renderCell: (item) => (
                <FC.TableCellLayout
                    truncate={false}
                    style={{ whiteSpace: 'normal', lineHeight: '18px', wordBreak: 'break-all', maxWidth: 120 }}
                >
                    {item.account}
                </FC.TableCellLayout>
            ),
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => "姓名",
            renderCell: (item) => (
                <FC.TableCellLayout
                    truncate={false}
                    style={{ whiteSpace: 'normal', lineHeight: '18px', wordBreak: 'break-word', maxWidth: 100 }}
                >
                    {item.name}
                </FC.TableCellLayout>
            ),
        }),
        FC.createTableColumn({
            columnId: "department",
            renderHeaderCell: () => "部门",
            renderCell: (item) => (
                <FC.TableCellLayout
                    truncate={false}
                    style={{ whiteSpace: 'normal', lineHeight: '18px', wordBreak: 'break-word', maxWidth: 120 }}
                >
                    {item.department}
                </FC.TableCellLayout>
            ),
        }),
        FC.createTableColumn({
            columnId: "owner_create",
            renderHeaderCell: () => {
                return "分配执行";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <FC.Switch
                            size="small"
                            disabled={!eligible.ownerCreater?.has(item.id)}
                            checked={ownerCreater === item.id}
                            onChange={(_, data) => {
                                if (data.checked) {
                                    setOwnerCreater(item.id);
                                } else if (ownerCreater === item.id) {
                                    setOwnerCreater(null);
                                }
                            }}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "owner_check",
            renderHeaderCell: () => {
                return "分配巡检";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <FC.Switch
                            size="small"
                            disabled={!eligible.ownerChecker?.has(item.id)}
                            checked={ownerChecker === item.id}
                            onChange={(_, data) => {
                                if (data.checked) {
                                    setOwnerChecker(item.id);
                                } else if (ownerChecker === item.id) {
                                    setOwnerChecker(null);
                                }
                            }}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "owner_annotate",
            renderHeaderCell: () => {
                return "分配标注";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <FC.Switch
                            size="small"
                            disabled={!eligible.ownerAnnotator?.has(item.id)}
                            checked={ownerAnnotator === item.id}
                            onChange={(_, data) => {
                                if (data.checked) {
                                    setOwnerAnnotator(item.id);
                                } else if (ownerAnnotator === item.id) {
                                    setOwnerAnnotator(null);
                                }
                            }}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "owner_recheck",
            renderHeaderCell: () => {
                return "分配复核";
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <FC.Switch
                            size="small"
                            disabled={!eligible.ownerRechecker?.has(item.id)}
                            checked={ownerReChecker === item.id}
                            onChange={(_, data) => {
                                if (data.checked) {
                                    setOwnerReChecker(item.id);
                                } else if (ownerReChecker === item.id) {
                                    setOwnerReChecker(null);
                                }
                            }}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <FC.Input
                appearance="underline"
                contentBefore={<IC.SearchRegular />}
                placeholder="快速搜索..."
                onChange={(evt) => {
                    setFilter(evt.target.value);
                }}
            />
            <div style={{ flex: 1 }}>
                <FC.DataGrid
                    items={users}
                    columns={columns}
                    getRowId={(item) => item.id}
                >
                    <FC.DataGridHeader>
                        <FC.DataGridRow>
                            {({ renderHeaderCell }, i) => (
                                <FC.DataGridHeaderCell key={i}>
                                    {renderHeaderCell()}
                                </FC.DataGridHeaderCell>
                            )}
                        </FC.DataGridRow>
                    </FC.DataGridHeader>
                    <FC.DataGridBody>
                        {({ item }) => (
                            <FC.DataGridRow key={item.seq}>
                                {({ renderCell }) => (
                                    <FC.DataGridCell>
                                        {renderCell(item)}
                                    </FC.DataGridCell>
                                )}
                            </FC.DataGridRow>
                        )}
                    </FC.DataGridBody>
                </FC.DataGrid>
            </div>
        </div>
    );
};

const TaskStatusSelector = ({ task, onChange, taskInfo }) => {
    const divider = () => (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                height: 1,
                color: FC.tokens.colorBrandBackground,
            }}
        >
            <div
                style={{
                    flex: 1,
                    height: 1,
                    backgroundColor: FC.tokens.colorBrandBackground,
                }}
            ></div>
            <IC.ArrowRightFilled />
        </div>
    );

    let curStatus = taskInfo?.status || task?.status;

    const node = (title, status) => {
        let owner = null;

        if (status === "create") {
            owner = task?.owner_creater?.name;
        }

        if (status === "check") {
            owner = task?.owner_checker?.name;
        }

        if (status === "annotation") {
            owner = task?.owner_annotator?.name;
        }

        if (status === "recheck") {
            owner = task?.owner_rechecker?.name;
        }

        let isMyStatus = curStatus === status;

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    padding: isMyStatus ? 10 : null,
                    boxShadow: isMyStatus ? FC.tokens.shadow4 : null,
                    zoom: isMyStatus ? 1.2 : 1.0,
                }}
            >
                <FC.Label>{title}</FC.Label>
                {owner && <FC.Label>{owner}</FC.Label>}
            </div>
        );
    };

    return (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                // justifyContent: "space-between",
                // padding: 10,
                // alignItems: "center",
                // gap: 10,
            }}
        >
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    padding: 10,
                    alignItems: "center",
                    gap: 10,
                }}
            >
                {task && (
                    <Fragment>
                        {node("1.创建", "create")}
                        {divider()}
                        {node("2.巡检", "check")}
                        {divider()}
                        {node("3.后台数据处理", "annotation")}
                        {divider()}
                        {node("4.复核", "recheck")}
                        {divider()}
                        {task.status === "deleted"
                            ? node("5.删除", "deleted")
                            : node("5.完成", "done")}
                    </Fragment>
                )}
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    gap: 10,
                }}
            >
                <FC.Button
                    onClick={(evt) => {
                        onChange("check");
                    }}
                    disabled={curStatus === "check"}
                >
                    标记巡检
                </FC.Button>
                <FC.Button
                    onClick={(evt) => {
                        onChange("annotation");
                    }}
                    disabled={curStatus === "annotation"}
                >
                    标记后台
                </FC.Button>
                <FC.Button
                    onClick={(evt) => {
                        onChange("recheck");
                    }}
                    disabled={curStatus === "recheck"}
                >
                    标记复核
                </FC.Button>
                <FC.Button
                    onClick={(evt) => {
                        onChange("done");
                    }}
                    disabled={["done"].indexOf(curStatus) >= 0}
                >
                    标记完成
                </FC.Button>
            </div>
        </div>
    );
};

const RawDataList = ({ task }) => {
    let [items, setItems] = useState([]);
    let { apiClient } = useContext(Context);

    const getRawData = async () => {
        try {
            if (!task || !task.id) {
                setItems([]);
                return;
            }
            
            // Get raw data for this task from API
            // Note: You may need to add a getRawDataByTask method to apiClient
            // For now, using a generic approach
            const rawData = await apiClient.getRawData({ taskId: task.id });
            
            // Sort by uploaded_at in descending order
            const sortedData = (rawData || []).sort((a, b) => 
                new Date(b.uploaded_at) - new Date(a.uploaded_at)
            );
            
            setItems(sortedData);
        } catch (error) {
            console.error("Failed to load raw data:", error);
            setItems([]);
        }
    };

    useEffect(() => {
        if (apiClient && task) {
            getRawData();
        }
    }, [apiClient, task]);

    const createCard = (item, idx) => {
        let name = "..." + item.name.substring(item.name.length - 5);
        return (
            <div
                key={idx}
                style={{
                    padding: 10,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    borderRadius: 5,
                    boxShadow: FC.tokens.shadow2,
                    backgroundColor: "white",
                }}
            >
                <span>原始文件</span>
                <span>{name}</span>
                <span>风机部位</span>
                <span>{cvtLeafPart(item.part)}</span>
                <span>数据状态</span>
                <span>{cvtRawDataStatus(item.status)}</span>
            </div>
        );
    };

    return (
        <div
            style={{
                flex: 1,
                display: "grid",
                gap: 10,
                padding: 10,
                gridTemplateColumns: "1fr 1fr",
                overflowY: "auto",
                backgroundColor: "#ccc",
            }}
        >
            {items.map((item, idx) => createCard(item, idx))}
        </div>
    );
};

export const TaskDetail = () => {
    let [task, setTask] = useState(null);
    let [field, setField] = useState(null);
    let [taskInfo, setTaskInfo] = useState(null);
    let [availableCustomers, setAvailableCustomers] = useState([]);
    let [tenants, setTenants] = useState([]);
    let [assetOwners, setAssetOwners] = useState([]);
    let [customerLoading, setCustomerLoading] = useState(false);
    let [customerError, setCustomerError] = useState(null);
    let { apiClient, currentField } = useContext(Context);
    let { taskId } = useParams();
    const [searchParams] = useSearchParams();
    const managedTenantCandidate = Number(searchParams.get("tenantId"));
    const managedTenantId = searchParams.get("managed") === "1" &&
        Number.isInteger(managedTenantCandidate) && managedTenantCandidate > 0
        ? managedTenantCandidate
        : null;
    const [managedFields, setManagedFields] = useState([]);
    let nav = useNavigate();
    // UX only; the backend policies and tenant filter are the real boundary.
    const equipmentPermissions = useMemo(() => resolveEquipmentPermissions(getCurrentUser()), []);
    useEffect(() => {
        if (managedTenantId) {
            return;
        }
        // Use currentField from Context for task creation
        if (currentField) {
            console.log("Using currentField from Context:", currentField);
            setField(currentField);
        } else {
            let field = getCurrentFieldFromStorage();
            if (field) {
                console.log("Using field from storage:", field);
                setField(field);
            } else {
                console.log("No field available from storage");
                setField(null);
            }
        }
    }, [currentField, managedTenantId]);

    useEffect(() => {
        if (!apiClient || !managedTenantId || taskId) {
            return;
        }

        let active = true;
        apiClient.getManagedFields(managedTenantId)
            .then((items) => {
                if (!active) return;
                const nextFields = items || [];
                setManagedFields(nextFields);
                setField((current) =>
                    nextFields.find((item) => item.id?.toString() === current?.id?.toString()) ||
                    nextFields[0] ||
                    null
                );
            })
            .catch((error) => {
                if (active) {
                    console.error("Failed to load managed fields:", error);
                    setManagedFields([]);
                    setField(null);
                }
            });
        return () => { active = false; };
    }, [apiClient, managedTenantId, taskId]);

    useEffect(() => {
        if (!taskId && managedTenantId) {
            setTaskInfo((previous) => ({
                ...(previous || {}),
                tenant_id: managedTenantId.toString(),
            }));
        }
    }, [managedTenantId, taskId]);

    useEffect(() => {
        if (!apiClient || !field?.id || field.assetOwnerId) {
            return;
        }

        apiClient.getFieldItem(field.id)
            .then((freshField) => {
                if (freshField) {
                    setField(freshField);
                }
            })
            .catch((error) => console.error("Failed to refresh field owner:", error));
    }, [apiClient, field]);

    const customerTenantFilterId = useMemo(() => {
        if (taskInfo?.tenant_id) {
            return taskInfo.tenant_id.toString();
        }
        if (task?.tenantId) {
            return task.tenantId.toString();
        }
        return tenants[0]?.id ? tenants[0].id.toString() : "";
    }, [task, taskInfo, tenants]);

    const customerAssetOwnerFilterId = useMemo(() => {
        if (taskInfo?.asset_owner_id) {
            return taskInfo.asset_owner_id.toString();
        }
        if (field?.assetOwnerId) {
            return field.assetOwnerId.toString();
        }
        if (task?.assetOwnerId) {
            return task.assetOwnerId.toString();
        }
        return assetOwners[0]?.id ? assetOwners[0].id.toString() : "";
    }, [assetOwners, field, task, taskInfo]);

    useEffect(() => {
        const fetchCustomers = async () => {
            if (!customerTenantFilterId) {
                setAvailableCustomers([]);
                return;
            }

            setCustomerLoading(true);
            setCustomerError(null);
            try {
                const response = await customerService.list(
                    { includeDisabled: true, tenantId: customerTenantFilterId },
                    apiClient,
                );
                const normalized = (response || [])
                    .map((customer) => ({
                        ...customer,
                        id: customer.id,
                        tenantId: customer.tenantId ?? customer.tenant_id,
                        assetOwnerId: customer.assetOwnerId ?? customer.asset_owner_id,
                        companyName: customer.companyName ?? customer.company_name,
                        customerCode: customer.customerCode ?? customer.customer_code,
                        disabled: Boolean(customer.disabled),
                    }))
                    .filter((customer) =>
                        !customerAssetOwnerFilterId ||
                        customer.assetOwnerId?.toString() === customerAssetOwnerFilterId
                    );
                setAvailableCustomers(normalized);
            } catch (error) {
                console.error("Failed to load customers:", error);
                setCustomerError("加载客户列表失败，请稍后重试。");
                setAvailableCustomers([]);
            } finally {
                setCustomerLoading(false);
            }
        };

        if (apiClient) {
            fetchCustomers();
        }
    }, [apiClient, customerTenantFilterId, customerAssetOwnerFilterId]);

    useEffect(() => {
        if (!apiClient) {
            return;
        }

        if (managedTenantId) {
            apiClient.getTenant(managedTenantId)
                .then((item) => setTenants(item ? [item] : []))
                .catch((error) => console.error("Failed to load managed tenant:", error));
            apiClient.getManagedAssetOwners(managedTenantId)
                .then((items) => setAssetOwners((items || [])
                    .filter((item) => item.effectiveStatus === "active")
                    .map((item) => ({
                        id: item.assetOwnerId,
                        seq: item.assetOwnerSeq,
                        name: item.assetOwnerName,
                    }))))
                .catch((error) => console.error("Failed to load managed asset owners:", error));
            return;
        }

        apiClient.getTenants({ status: "active" })
            .then((items) => setTenants(items || []))
            .catch((error) => console.error("Failed to load tenants:", error));

        apiClient.getAssetOwners({ status: "active" })
            .then((items) => setAssetOwners(items || []))
            .catch((error) => console.error("Failed to load asset owners:", error));
    }, [apiClient, managedTenantId]);

    const selectedCustomerId = useMemo(() => {
        if (taskInfo?.customer_id) {
            return taskInfo.customer_id.toString();
        }
        const sessionCustomerId =
            task?.session?.customerId ?? task?.session?.customer_id;
        return sessionCustomerId ? sessionCustomerId.toString() : "";
    }, [task, taskInfo]);

    const selectedCustomer = useMemo(() => {
        if (!selectedCustomerId) {
            return null;
        }

        return (
            availableCustomers.find(
                (customer) => customer.id?.toString() === selectedCustomerId,
            ) || null
        );
    }, [availableCustomers, selectedCustomerId]);

    useEffect(() => {
        if (
            taskId ||
            customerLoading ||
            !taskInfo?.customer_id ||
            availableCustomers.some((customer) => customer.id?.toString() === taskInfo.customer_id.toString())
        ) {
            return;
        }

        setTaskInfo((prev) => ({ ...(prev || {}), customer_id: "" }));
    }, [availableCustomers, customerLoading, taskId, taskInfo?.customer_id]);

    const selectedTenantId = useMemo(() => {
        return customerTenantFilterId;
    }, [customerTenantFilterId]);

    const selectedAssetOwnerId = useMemo(() => {
        return customerAssetOwnerFilterId;
    }, [customerAssetOwnerFilterId]);

    const customerMessage = useMemo(() => {
        if (customerLoading) {
            return "正在加载客户列表...";
        }

        if (customerError) {
            return customerError;
        }

        if (availableCustomers.length === 0) {
            return "暂无可用客户，请先在客户管理中创建客户。";
        }

        if (!selectedCustomerId) {
            return "请选择客户（必填）。";
        }

        if (selectedCustomer?.disabled) {
            return "所选客户已禁用，请选择其他客户。";
        }

        if (selectedCustomer) {
            const name =
                selectedCustomer.companyName ||
                selectedCustomer.customerCode ||
                "未命名客户";
            return `已选择：${name}`;
        }

        return "";
    }, [
        availableCustomers.length,
        customerError,
        customerLoading,
        selectedCustomer,
        selectedCustomerId,
    ]);

    const customerMessageColor = useMemo(() => {
        if (customerError || selectedCustomer?.disabled) {
            return "#d13438";
        }

        if (!customerLoading && availableCustomers.length === 0) {
            return "#d13438";
        }

        if (!selectedCustomerId) {
            return customerLoading ? "#999" : "#d13438";
        }

        return "#333";
    }, [
        availableCustomers.length,
        customerError,
        customerLoading,
        selectedCustomer,
        selectedCustomerId,
    ]);

    const canSubmit = useMemo(() => {
        if (task) {
            return Boolean(taskInfo);
        }

        if (!taskInfo) {
            return false;
        }

        return (
            Boolean(selectedCustomerId) &&
            Boolean(selectedTenantId) &&
            Boolean(selectedAssetOwnerId) &&
            !customerLoading &&
            !customerError &&
            availableCustomers.length > 0 &&
            !(selectedCustomer && selectedCustomer.disabled)
        );
    }, [
        availableCustomers.length,
        customerError,
        customerLoading,
        selectedCustomer,
        selectedCustomerId,
        selectedTenantId,
        selectedAssetOwnerId,
        task,
        taskInfo,
    ]);

    useEffect(() => {
        if (
            !taskId &&
            taskInfo &&
            !taskInfo.customer_id &&
            availableCustomers.length === 1
        ) {
            const defaultId = availableCustomers[0].id?.toString();
            if (defaultId) {
                setTaskInfo((prev) => ({ ...(prev || {}), customer_id: defaultId }));
            }
        }
    }, [availableCustomers, taskId, taskInfo]);

    useEffect(() => {
        if (!taskId && taskInfo && !taskInfo.tenant_id && tenants.length === 1) {
            const defaultId = tenants[0].id?.toString();
            if (defaultId) {
                setTaskInfo((prev) => ({ ...(prev || {}), tenant_id: defaultId }));
            }
        }
    }, [tenants, taskId, taskInfo]);

    useEffect(() => {
        if (!taskId && field?.assetOwnerId) {
            setTaskInfo((prev) => ({
                ...(prev || {}),
                asset_owner_id: field.assetOwnerId.toString(),
            }));
        }
    }, [field, taskId]);

    const updateInfo = (key) => {
        return (evt) => {
            let kv = {};
            kv[key] = evt.target.value;
            setTaskInfo((prev) => ({ ...(prev || {}), ...kv }));
        };
    };
    const deleteTask = async () => {
        if (!task) {
            return;
        }
        try {
            await apiClient.deleteTask(task.id);
        } catch (e) {
            console.error("Failed to delete task:", e);
            alert("Failed to delete task");
            return;
        }
        nav(-1);
    };

    const convertPriorityToString = (priority) => {
        // Convert numeric priority to string if needed
        if (typeof priority === 'number') {
            switch(priority) {
                case 3: return 'high';
                case 2: return 'medium';
                case 1: return 'low';
                default: return 'normal';
            }
        }
        return priority || 'normal';
    };

    const saveTask = async () => {
        if (task) {
            try {
                let check_at = undefined;
                let recheck_at = undefined;
                if (taskInfo?.status) {
                    if (taskInfo.status === "check") {
                        check_at = new Date();
                    }

                    if (taskInfo.status === "recheck") {
                        recheck_at = new Date();
                    }
                }

                let turbines = (taskInfo.turbines || []).map((it) =>
                    typeof it === 'string' ? it : it.id,
                );
                let owner_checker = taskInfo.owner_checker;
                let owner_annotator = taskInfo.owner_annotator;
                let owner_rechecker = taskInfo.owner_rechecker;
                let owner_creater = taskInfo.owner_creater;

                // Map to backend expected field names (PascalCase)
                const updateTaskData = {
                    Name: taskInfo.name,
                    Priority: taskInfo.priority ? convertPriorityToString(taskInfo.priority) : undefined,
                    Status: taskInfo.status,
                    TypeRecheck: taskInfo.type_recheck,
                    CompletedAt: taskInfo.completed_at || null,
                    // Description is now automatically handled by backend with tool configuration
                    // Description: taskInfo.description,
                    OwnerCreaterId: owner_creater ? parseInt(owner_creater, 10) : 0,
                    OwnerCheckerId: owner_checker ? parseInt(owner_checker, 10) : 0,
                    OwnerAnnotatorId: owner_annotator ? parseInt(owner_annotator, 10) : 0,
                    OwnerRecheckerId: owner_rechecker ? parseInt(owner_rechecker, 10) : 0
                };
                // Only include TurbineIds when the single turbine is actually being changed. Sending the
                // unchanged id would turn an ordinary metadata edit on a divergent task into a new
                // divergence declaration.
                if (Array.isArray(turbines) && turbines.length === 1) {
                    const originalTurbines = (task.turbines || []).map((it) =>
                        typeof it === 'string' ? it : it.id,
                    );
                    if (originalTurbines.length !== 1 || originalTurbines[0].toString() !== turbines[0].toString()) {
                        updateTaskData.TurbineIds = turbines;
                        updateTaskData.AssetOwnerDivergenceReason =
                            taskInfo.asset_owner_divergence_reason?.trim() || undefined;
                    }
                }
                
                // Remove null/undefined values
                Object.keys(updateTaskData).forEach(key => {
                    if (updateTaskData[key] === null || updateTaskData[key] === undefined) {
                        delete updateTaskData[key];
                    }
                });
                
                await apiClient.updateTask(task.id, updateTaskData);
            } catch (e) {
                console.error("failed to save task", e);
                alert("failed to save task");
            } finally {
                setTaskInfo(null);
                nav(-1);
            }
            return;
        }

        try {
            let turbineIds = (taskInfo.turbines || []).map((it) =>
                typeof it === 'string' ? parseInt(it, 10) : it.id
            );

            if (!Array.isArray(turbineIds) || turbineIds.length === 0 || turbineIds.some(isNaN)) {
                alert('请选择至少一个风机以创建巡检会话。');
                return;
            }

            if (customerLoading) {
                alert('客户列表正在加载，请稍候。');
                return;
            }

            if (customerError) {
                alert(customerError);
                return;
            }

            const customerIdValue = taskInfo.customer_id;
            const customerIdNumber = customerIdValue ? parseInt(customerIdValue, 10) : NaN;

            if (!customerIdValue || Number.isNaN(customerIdNumber)) {
                alert('请选择客户以创建巡检会话。');
                return;
            }

            if (selectedCustomer?.disabled) {
                alert('所选客户已禁用，请选择其他客户。');
                return;
            }

            const tenantIdNumber = selectedTenantId ? parseInt(selectedTenantId, 10) : NaN;
            const assetOwnerIdNumber = selectedAssetOwnerId ? parseInt(selectedAssetOwnerId, 10) : NaN;
            if (Number.isNaN(tenantIdNumber) || Number.isNaN(assetOwnerIdNumber)) {
                alert('请选择租户和业主以创建巡检会话。');
                return;
            }

            const fieldAssetOwnerId = Number(field?.assetOwnerId);
            const ownerDiverged = Number.isFinite(fieldAssetOwnerId) &&
                assetOwnerIdNumber !== fieldAssetOwnerId;
            const divergenceReason = taskInfo.asset_owner_divergence_reason?.trim();
            if (ownerDiverged && !divergenceReason) {
                alert('所选数据归属方与风场业主不同时，必须填写归属分歧理由。');
                return;
            }

            // ALWAYS create a session, regardless of turbine count (1 or multiple)
            // No task should exist without a session
            const sessionPayload = {
                SessionName: taskInfo.name || taskInfo.seq || 'inspection_session',
                FieldItemId: field?.id,
                TenantId: tenantIdNumber,
                AssetOwnerId: assetOwnerIdNumber,
                AssetOwnerDivergenceReason: ownerDiverged ? divergenceReason : null,
                TurbineIds: turbineIds,
                InspectionDate: taskInfo.completed_at || new Date().toISOString(),
                // pass owners from taskInfo (WorkerSelectorSimple sets these)
                OwnerCreaterId: taskInfo.owner_creater ? parseInt(taskInfo.owner_creater, 10) : null,
                OwnerCheckerId: taskInfo.owner_checker ? parseInt(taskInfo.owner_checker, 10) : null,
                OwnerAnnotatorId: taskInfo.owner_annotator ? parseInt(taskInfo.owner_annotator, 10) : null,
                OwnerRecheckerId: taskInfo.owner_rechecker ? parseInt(taskInfo.owner_rechecker, 10) : null,
                CustomerId: customerIdNumber,
            };

            console.log("Creating inspection session with data:", sessionPayload);
            const created = await apiClient.createInspectionSession(sessionPayload);

            setTaskInfo(null);

            // Land on the first real turbine task. Sessions no longer have a synthetic ROOT task.
            const firstTaskId = created?.tasks?.[0]?.taskId;
            if (firstTaskId) {
                nav(managedTenantId
                    ? `/task/${firstTaskId}?tenantId=${managedTenantId}&managed=1`
                    : `/task/${firstTaskId}`);
            } else {
                nav('/task/check');
            }
        } catch (e) {
            console.error("failed to save task", e);
            console.error("Error details:", e.message, e.response);
            alert("failed to create: " + (e.message || "Unknown error"));
            // Deliberately no navigation here. Leaving the page on a failure used to discard
            // whatever the user had entered and send them to a list that would not show the
            // session they were told had failed.
        }
    };

    const getTask = async () => {
        try {
            console.log("taskId:", taskId);
            
            // Get task details from API
            const task = await apiClient.getTask(taskId);
            
            if (task && (!managedTenantId || Number(task.tenantId ?? task.tenant_id) === managedTenantId)) {
                // The API should already include all related data (field_item, owners, turbines)
                // If not, you may need to make additional calls to fetch related data
                console.log("task:", task);
                setTask(task);
            } else {
                console.error("Task not found:", taskId);
                setTask(null);
            }
        } catch (error) {
            console.error("Failed to load task:", error);
            setTask(null);
        }
    };

    useEffect(() => {
        if (apiClient && taskId) {
            getTask();
        }
    }, [apiClient, managedTenantId, taskId]);

    // Initialize taskInfo when task is loaded
    useEffect(() => {
        if (task && !taskInfo) {
            setTaskInfo({
                seq: task.seq,
                name: task.name,
                priority: task.priority,
                status: task.status,
                type_recheck: task.type_recheck,
                completed_at: task.completed_at,
                owner_checker: task.owner_checker?.id,
                owner_annotator: task.owner_annotator?.id,
                owner_rechecker: task.owner_rechecker?.id,
                turbines: task.turbines || [],
                customer_id:
                    task.session?.customerId?.toString() ||
                    task.session?.customer_id?.toString() ||
                    "",
            });
        }
    }, [task, taskInfo]);

    let styles = useStyleTaskDetail();
    return (
        <div className={styles.root} style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <h1 style={{ margin: "10px 20px", height: "40px" }}>
                {taskId ? `编辑任务 ${task?.seq || ''}` : "创建任务"}
            </h1>

            {/* Upper section - increase height so due date is visible */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "15px",
                    padding: "0 20px",
                    height: "32%",
                    minHeight: "260px",
                    maxHeight: "360px",
                }}
            >
                {/* Column 1: Basic Information */}
                <div style={{
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "6px",
                    boxShadow: FC.tokens.shadow4,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: "10px", fontSize: "14px" }}>基本信息</h3>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                        gap: "10px",
                        fontSize: "12px",
                        overflow: "auto",
                        flex: 1,
                        padding: "0 2px"
                    }}>
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>任务编号</FC.Label>
                            <FC.Input
                                size="small"
                                style={{ width: "100%" }}
                                value={taskInfo?.seq || task?.seq || ""}
                                onChange={updateInfo("seq")}
                            />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>任务名称</FC.Label>
                            <FC.Input
                                size="small"
                                style={{ width: "100%" }}
                                value={taskInfo?.name || task?.name || ""}
                                onChange={updateInfo("name")}
                            />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>租户</FC.Label>
                            <FC.Dropdown
                                size="small"
                                style={{ width: "100%" }}
                                disabled={Boolean(taskId) || Boolean(managedTenantId)}
                                selectedOptions={selectedTenantId ? [selectedTenantId] : []}
                                value={
                                    tenants.find((tenant) => tenant.id?.toString() === selectedTenantId)?.name ||
                                    "选择租户"
                                }
                                onOptionSelect={(_, data) => {
                                    if (!data.optionValue) {
                                        return;
                                    }
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        tenant_id: data.optionValue,
                                        customer_id: "",
                                    }));
                                }}
                            >
                                {tenants.map((tenant) => (
                                    <FC.Option key={tenant.id} value={tenant.id?.toString()}>
                                        {`${tenant.seq} - ${tenant.name}`}
                                    </FC.Option>
                                ))}
                            </FC.Dropdown>
                        </div>
                        {managedTenantId && !taskId && (
                            <div style={{ minWidth: 0 }}>
                                <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>风场</FC.Label>
                                <FC.Dropdown
                                    size="small"
                                    style={{ width: "100%" }}
                                    selectedOptions={field?.id ? [field.id.toString()] : []}
                                    value={field ? fieldLabel(field) : "选择风场"}
                                    onOptionSelect={(_, data) => {
                                        const next = managedFields.find((item) => item.id?.toString() === data.optionValue);
                                        setField(next || null);
                                        setTaskInfo((previous) => ({
                                            ...(previous || {}),
                                            asset_owner_id: next?.assetOwnerId?.toString() || "",
                                            customer_id: "",
                                            turbines: [],
                                        }));
                                    }}
                                >
                                    {managedFields.map((item) => (
                                        <FC.Option key={item.id} value={item.id?.toString()}>{fieldLabel(item)}</FC.Option>
                                    ))}
                                </FC.Dropdown>
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>业主</FC.Label>
                            <FC.Dropdown
                                size="small"
                                style={{ width: "100%" }}
                                disabled={Boolean(taskId)}
                                selectedOptions={selectedAssetOwnerId ? [selectedAssetOwnerId] : []}
                                value={
                                    assetOwners.find((owner) => owner.id?.toString() === selectedAssetOwnerId)?.name ||
                                    "选择业主"
                                }
                                onOptionSelect={(_, data) => {
                                    if (!data.optionValue) {
                                        return;
                                    }
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        asset_owner_id: data.optionValue,
                                        customer_id: "",
                                    }));
                                }}
                            >
                                {assetOwners.map((owner) => (
                                    <FC.Option key={owner.id} value={owner.id?.toString()}>
                                        {`${owner.seq} - ${owner.name}`}
                                    </FC.Option>
                                ))}
                            </FC.Dropdown>
                        </div>
                        {((!taskId &&
                            selectedAssetOwnerId &&
                            field?.assetOwnerId &&
                            selectedAssetOwnerId !== field.assetOwnerId.toString()) || taskId) && (
                            <div style={{ minWidth: 0 }}>
                                <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>
                                    {taskId ? "变更风机时的归属分歧理由（如适用）" : "归属分歧理由（必填）"}
                                </FC.Label>
                                <FC.Input
                                    size="small"
                                    style={{ width: "100%" }}
                                    value={taskInfo?.asset_owner_divergence_reason || ""}
                                    onChange={(_, data) => setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        asset_owner_divergence_reason: data.value,
                                    }))}
                                />
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>客户</FC.Label>
                            <FC.Dropdown
                                size="small"
                                style={{
                                    width: "100%",
                                    border:
                                        !task &&
                                        !customerLoading &&
                                        !selectedCustomerId
                                            ? "1px solid #d13438"
                                            : undefined,
                                }}
                                disabled={customerLoading || Boolean(taskId)}
                                selectedOptions={selectedCustomerId ? [selectedCustomerId] : []}
                                value={
                                    selectedCustomer
                                        ? `${selectedCustomer.companyName || "-"} (${selectedCustomer.customerCode || "无编号"})`
                                        : ""
                                }
                                onOptionSelect={(_, data) => {
                                    if (
                                        !data.optionValue ||
                                        data.optionValue === "__loading" ||
                                        data.optionValue === "__empty"
                                    ) {
                                        return;
                                    }
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        customer_id: data.optionValue,
                                    }));
                                }}
                            >
                                {customerLoading && (
                                    <FC.Option value="__loading" disabled>
                                        正在加载...
                                    </FC.Option>
                                )}
                                {!customerLoading && availableCustomers.length === 0 && (
                                    <FC.Option value="__empty" disabled>
                                        暂无可用客户
                                    </FC.Option>
                                )}
                                {!customerLoading &&
                                    availableCustomers.map((customer) => (
                                        <FC.Option
                                            key={customer.id}
                                            value={customer.id?.toString()}
                                        >
                                            {`${customer.companyName || "-"} (${customer.customerCode || "无编号"})${customer.disabled ? " - 已禁用" : ""}`}
                                        </FC.Option>
                                    ))}
                            </FC.Dropdown>
                            <div
                                style={{
                                    marginTop: "4px",
                                    fontSize: "12px",
                                    color: customerMessageColor,
                                }}
                            >
                                {customerMessage}
                            </div>
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>复核类型</FC.Label>
                            <FC.Dropdown
                                size="small"
                                style={{ width: "100%" }}
                            value={[
                                taskInfo?.type_recheck || task?.type_recheck
                                    ? cvtTaskRecheckType(
                                          taskInfo?.type_recheck ||
                                              task?.type_recheck,
                                      )
                                    : "",
                            ]}
                            onOptionSelect={(evt, data) => {
                                let type_recheck = cvtTaskRecheckType(
                                    data.optionValue,
                                );
                                setTaskInfo((prev) => ({
                                    ...(prev || {}),
                                    type_recheck,
                                }));
                            }}
                        >
                            {["full", "partial"].map((p) => {
                                p = cvtTaskRecheckType(p);
                                return <FC.Option key={p}>{p}</FC.Option>;
                            })}
                        </FC.Dropdown>
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>任务优先级</FC.Label>
                            <FC.Dropdown
                                size="small"
                                style={{ width: "100%" }}
                                value={[
                                    taskInfo?.priority || task?.priority
                                        ? cvtPriority(
                                              taskInfo?.priority || task?.priority,
                                          )
                                        : "",
                                ]}
                                onOptionSelect={(evt, data) => {
                                    let priority = cvtPriority(data.optionValue);
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        priority,
                                    }));
                                }}
                            >
                                {[1, 2, 3].map((p) => {
                                    p = cvtPriority(p);
                                    return <FC.Option key={p}>{p}</FC.Option>;
                                })}
                            </FC.Dropdown>
                        </div>
                        <div style={{ gridColumn: "span 2", minWidth: 0 }}>
                            <FC.Label style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>任务预定完成时间</FC.Label>
                            <FDP.DatePicker
                                size="small"
                                style={{ width: "100%" }}
                                formatDate={toDateString}
                                onSelectDate={(date) => {
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        completed_at: date,
                                    }));
                                }}
                                value={
                                    (taskInfo?.completed_at &&
                                        new Date(taskInfo.completed_at)) ||
                                    (task?.completed_at &&
                                        new Date(task?.completed_at)) ||
                                    null
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Column 2: Personnel Arrangement */}
                <div style={{
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "6px",
                    boxShadow: FC.tokens.shadow4,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: "10px", fontSize: "14px" }}>人员安排</h3>
                    <div style={{ overflow: "auto", flex: 1 }}>
                        <WorkerSelectorSimple
                            task={task}
                            tenantId={customerTenantFilterId}
                            onSelect={(owners) => {
                                // owners may include ids and display names
                                setTaskInfo((prev) => ({
                                    ...(prev || {}),
                                    ...owners,
                                }));
                            }}
                        />
                    </div>
                </div>

                {/* Column 3: Task Data (placeholder for future) */}
                <div style={{
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "6px",
                    boxShadow: FC.tokens.shadow4,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: "10px", fontSize: "14px" }}>任务数据</h3>
                    <div style={{ overflow: "auto", flex: 1 }}>
                        {task && task.raw_data && task.raw_data.length > 0 ? (
                            <div>
                                <FC.Text size={300}>共 {task.raw_data.length} 个文件</FC.Text>
                                <div style={{ marginTop: "10px", fontSize: "12px" }}>
                                    {task.raw_data.slice(0, 3).map((data, idx) => (
                                        <div key={idx} style={{ marginBottom: "5px" }}>
                                            • {data.name?.substring(data.name.length - 20) || `文件${idx + 1}`}
                                        </div>
                                    ))}
                                    {task.raw_data.length > 3 && (
                                        <div>• 及其他 {task.raw_data.length - 3} 个文件...</div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <FC.Text size={300} style={{ color: "#999" }}>暂无数据文件</FC.Text>
                        )}
                    </div>
                </div>
            </div>

            {/* Lower section - 75% for turbine management */}
            <div style={{
                flex: 1,
                padding: "20px",
                paddingTop: "10px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}>
                <h2 style={{ marginTop: 0, marginBottom: "10px", fontSize: "16px" }}>风机管理</h2>
                <div style={{
                    flex: 1,
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: FC.tokens.shadow4,
                    overflow: "hidden"
                }}>
                    {!taskId && (
                        <div style={{ padding: "20px" }}>
                            <FC.Label>选择风场</FC.Label>
                            <FieldDropdown setField={setField} />
                            <div style={{ marginTop: "20px" }}>
                                <TurbineSelectorSimple
                                    field={field}
                                    onSelect={(turbines) => {
                                    setTaskInfo((prev) => ({
                                        ...(prev || {}),
                                        turbines,
                                    }));
                                    }}
                                    initialSelected={[]}
                                />
                            </div>
                        </div>
                    )}
                    {task && (
                        <TurbineTaskStatusView
                            task={task}
                            session={task.session}
                            taskInfo={taskInfo}
                            onStatusChange={(turbineId, status) => {
                                console.log('Status changed for turbine', turbineId, 'to', status);
                                // Update taskInfo with new status
                                setTaskInfo(prev => ({ ...prev, status: status }));
                                // Also refresh task data
                                getTask();
                            }}
                        />
                    )}
                </div>
            </div>
            {/* Which physical kit this job used (design 8.6). Attached to the existing session
                rather than to the creation form: the session is created first and the kit recorded
                against it, so a failed or skipped second step leaves EquipmentSetupStatus at
                pending and the entry can be completed later from here. Hidden for roles with no
                equipment access so they are not shown a panel that can only answer 403. */}
            {task?.session?.id && equipmentPermissions.canRead && (
                <div style={{ padding: "0 20px 10px 20px" }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: FC.tokens.shadow4,
                        padding: "12px",
                    }}>
                        <SessionEquipmentPanel
                            key={task.session.id}
                            sessionId={task.session.id}
                        />
                    </div>
                </div>
            )}
            {/* Bottom buttons */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                    justifyContent: "end",
                    padding: "15px 20px",
                    borderTop: "1px solid #e0e0e0",
                    backgroundColor: "#f5f5f5",
                }}
            >
                <FC.Button
                    appearance="primary"
                    disabled={!canSubmit}
                    onClick={async () => await saveTask()}
                >
                    {task ? "保存修改" : "创建任务"}
                </FC.Button>
                {task && (
                    <FC.Button
                        onClick={async () => {
                            if (window.confirm('确定要删除这个任务吗？')) {
                                await deleteTask();
                            }
                        }}
                    >
                        删除
                    </FC.Button>
                )}
                <FC.Button onClick={() => nav(-1)}>
                    取消
                </FC.Button>
            </div>
        </div>
    );
};

const isAdminUser = (user) => {
    const groups = Array.isArray(user?.groups) ? user.groups : [];
    return (
        groups.some((group) => String(group).toLowerCase() === "admin") ||
        String(user?.role || "").toLowerCase() === "admin"
    );
};

const tenantLabel = (tenant) => {
    if (!tenant) return "";
    return `${tenant.seq || tenant.id} - ${tenant.name || ""}`;
};

const fieldLabel = (field) => {
    if (!field) return "";
    return `${field.seq || field.id} - ${field.name || ""}`;
};

export const TaskManagement = () => {
    let [filter, setFilter] = useState(null);
    let [tasks, setTasks] = useState([]);
    let [tenants, setTenants] = useState([]);
    let [fields, setFields] = useState([]);
    const [searchParams] = useSearchParams();
    const managedTenantCandidate = Number(searchParams.get("tenantId"));
    const managedTenantId = searchParams.get("managed") === "1" &&
        Number.isInteger(managedTenantCandidate) && managedTenantCandidate > 0
        ? managedTenantCandidate.toString()
        : "";
    let [tenantFilterId, setTenantFilterId] = useState(managedTenantId);
    let [fieldFilterId, setFieldFilterId] = useState("");
    let nav = useNavigate();
    let { apiClient, currentField, user } = useContext(Context);
    const isAdmin = isAdminUser(user);

    const onCreateClick = () => {
        nav(managedTenantId
            ? `/task/new?tenantId=${managedTenantId}&managed=1`
            : "/task/new");
    };

    const searchable = [
        "seq",
        "name",
        "owner_creater.name",
        // "owner_checker.name",
        // "owner_rechecker.name",
        // "owner_creater.account",
        // "owner_checker.account",
        // "owner_rechecker.account",
    ];

    const listTenants = async () => {
        if (!apiClient || !isAdmin) {
            setTenants([]);
            return;
        }

        try {
            if (managedTenantId) {
                const tenant = await apiClient.getTenant(Number(managedTenantId));
                setTenants(tenant ? [tenant] : []);
            } else {
                const tenantItems = await apiClient.getTenants({ status: "active" });
                setTenants(tenantItems || []);
            }
        } catch (error) {
            console.error("Failed to load tenants:", error);
            setTenants([]);
        }
    };

    const listFields = async () => {
        if (!apiClient) {
            setFields([]);
            return;
        }

        try {
            const params = {};
            if (isAdmin && tenantFilterId) {
                params.tenantId = tenantFilterId;
            }

            const fieldItems = await apiClient.getFieldItems(params);
            const nextFields = fieldItems || [];
            setFields(nextFields);

            const currentFieldId = !tenantFilterId && currentField?.id ? currentField.id.toString() : "";
            setFieldFilterId((previous) => {
                if (previous && nextFields.some((field) => field.id?.toString() === previous)) {
                    return previous;
                }

                if (currentFieldId && nextFields.some((field) => field.id?.toString() === currentFieldId)) {
                    return currentFieldId;
                }

                return "";
            });
        } catch (error) {
            console.error("Failed to load field items:", error);
            setFields([]);
            setFieldFilterId("");
        }
    };

    const listTask = async () => {
        try {
            const params = {};
            if (filter) {
                params.search = filter;
            }
            if (isAdmin && tenantFilterId) {
                params.tenantId = tenantFilterId;
            }
            const fallbackFieldId = !tenantFilterId && currentField?.id ? currentField.id.toString() : "";
            const activeFieldId = fieldFilterId || fallbackFieldId;
            if (activeFieldId) {
                params.fieldId = activeFieldId;
            }
            let tasks = await apiClient.getTasks(params);
            tasks = tasks || [];
            setTasks(groupTasksBySession(tasks));
        } catch (error) {
            console.error("Failed to load tasks:", error);
            setTasks([]);
        }
    };

    useEffect(() => {
        listTenants();
    }, [apiClient, isAdmin]);

    useEffect(() => {
        listFields();
    }, [apiClient, isAdmin, tenantFilterId, currentField]);

    useEffect(() => {
        if (managedTenantId && tenantFilterId !== managedTenantId) {
            setTenantFilterId(managedTenantId);
            setFieldFilterId("");
        }
    }, [managedTenantId, tenantFilterId]);

    useEffect(() => {
        if (!isAdmin && !managedTenantId && tenantFilterId) {
            setTenantFilterId("");
        }
    }, [isAdmin, managedTenantId, tenantFilterId]);

    useEffect(() => {
        if (apiClient) {
            listTask();
        }
    }, [apiClient, filter, currentField, isAdmin, tenantFilterId, fieldFilterId]);

    let styles = useStyles();

    const columns = [
        ...(isAdmin ? [
            FC.createTableColumn({
                columnId: "tenant",
                renderHeaderCell: () => "租户",
                renderCell: (item) => {
                    return item.tenantName || item.tenant?.name || "-";
                },
            }),
        ] : []),
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => {
                return "批次编号";
            },
            renderCell: (item) => {
                return item.seq;
            },
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "批次名称";
            },
            renderCell: (item) => {
                return item?.name;
            },
        }),
        FC.createTableColumn({
            columnId: "customer",
            renderHeaderCell: () => "客户",
            renderCell: (item) => {
                return (
                    item.session?.customer?.companyName ||
                    item.session?.customer?.customerCode ||
                    "-"
                );
            },
        }),
        FC.createTableColumn({
            columnId: "owner_creater",
            renderHeaderCell: () => {
                return "现场执行";
            },
            renderCell: (item) => {
                return item.owner_creater?.name;
            },
        }),
        FC.createTableColumn({
            columnId: "owner_checker",
            renderHeaderCell: () => {
                return "巡检人";
            },
            renderCell: (item) => {
                return item.owner_checker?.name;
            },
        }),
        FC.createTableColumn({
            columnId: "owner_annotator",
            renderHeaderCell: () => {
                return "标注人";
            },
            renderCell: (item) => {
                return item.owner_annotator?.name;
            },
        }),
        FC.createTableColumn({
            columnId: "owner_rechecker",
            renderHeaderCell: () => {
                return "复核人";
            },
            renderCell: (item) => {
                return item.owner_rechecker?.name;
            },
        }),
        FC.createTableColumn({
            columnId: "progress",
            renderHeaderCell: () => "风机进度",
            renderCell: (item) => {
                return `${item.completedTurbines ?? 0}/${item.totalTurbines ?? item.taskCount ?? 0}`;
            },
        }),
        FC.createTableColumn({
            columnId: "created_at",
            renderHeaderCell: () => {
                return "创建于";
            },
            renderCell: (item) => {
                return new Date(item.created_at).toLocaleDateString();
            },
        }),
        FC.createTableColumn({
            columnId: "updated_at",
            renderHeaderCell: () => {
                return "更新于";
            },
            renderCell: (item) => {
                return new Date(item.updated_at).toLocaleDateString();
            },
        }),
        FC.createTableColumn({
            columnId: "priority",
            renderHeaderCell: () => {
                return "优先级";
            },
            renderCell: (item) => {
                return cvtPriority(item.priority);
            },
        }),
        FC.createTableColumn({
            columnId: "status",
            renderHeaderCell: () => {
                return "当前状态";
            },
            renderCell: (item) => {
                return cvtTaskStatus(item.status);
            },
        }),
        FC.createTableColumn({
            columnId: "more",
            renderHeaderCell: () => {
                return "更多操作";
            },
            renderCell: (item) => {
                return (
                    <FC.Button
                        onClick={() => {
                            console.log(item);
                            nav(managedTenantId
                                ? `/task/${idString(item.id)}?tenantId=${managedTenantId}&managed=1`
                                : `/task/${idString(item.id)}`);
                        }}
                        appearance="subtle"
                        icon={<IC.MoreCircleFilled />}
                    />
                );
            },
        }),
    ];

    return (
        <div className={styles.root}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <FC.Label>快速检索</FC.Label>
                <FC.Input
                    style={{ flex: "1 1 320px" }}
                    contentBefore={<IC.SearchRegular />}
                    placeholder="按批次编号、批次名称、现场执行人员搜索..."
                    onChange={(evt) => {
                        setFilter(evt.target.value);
                    }}
                />
                {isAdmin && (
                    <FC.Select
                        aria-label="租户筛选"
                        value={tenantFilterId}
                        disabled={Boolean(managedTenantId)}
                        onChange={(evt) => {
                            setTenantFilterId(evt.target.value);
                            setFieldFilterId("");
                        }}
                        style={{ minWidth: 240 }}
                    >
                        {!managedTenantId && <option value="">全部租户</option>}
                        {tenants.map((tenant) => (
                            <option key={tenant.id} value={tenant.id?.toString()}>
                                {tenantLabel(tenant)}
                            </option>
                        ))}
                    </FC.Select>
                )}
                <FC.Select
                    aria-label="风场筛选"
                    value={fieldFilterId}
                    onChange={(evt) => setFieldFilterId(evt.target.value)}
                    style={{ minWidth: 220 }}
                >
                    <option value="">全部风场</option>
                    {fields.map((field) => (
                        <option key={field.id} value={field.id?.toString()}>
                            {fieldLabel(field)}
                        </option>
                    ))}
                </FC.Select>
                <FC.Button appearance="primary" onClick={onCreateClick}>
                    创建巡检批次
                </FC.Button>
            </div>
            <FC.DataGrid columns={columns} items={tasks}>
                <FC.DataGridHeader>
                    <FC.DataGridRow>
                        {({ renderHeaderCell }, i) => (
                            <FC.DataGridHeaderCell key={i}>
                                {renderHeaderCell()}
                            </FC.DataGridHeaderCell>
                        )}
                    </FC.DataGridRow>
                </FC.DataGridHeader>
                <FC.DataGridBody>
                    {({ item }) => (
                        <FC.DataGridRow key={item.sessionId ?? item.seq}>
                            {({ renderCell }) => (
                                <FC.DataGridCell>
                                    {renderCell(item)}
                                </FC.DataGridCell>
                            )}
                        </FC.DataGridRow>
                    )}
                </FC.DataGridBody>
            </FC.DataGrid>
        </div>
    );
};
